import { useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Plus,
  Layers,
  Search,
  MoreHorizontal,
  Pencil,
  Trash2,
  Archive,
  ArchiveRestore,
  Building2,
  FolderOpen,
} from "lucide-react";
import {
  useProjectGroups,
  useCreateProjectGroup,
  useSoftDeleteProjectGroup,
  useRestoreProjectGroup,
  useUpdateProjectGroup,
  ProjectGroupWithRelations,
  type ProjectGroupInsert,
} from "@/hooks/useProjectGroups";
import { useCompanies } from "@/hooks/useCompanies";
import { ProjectGroupForm } from "@/components/forms/ProjectGroupForm";
import { toast } from "sonner";

const COLOR_CLASSES: Record<string, string> = {
  blue: "bg-blue-500",
  green: "bg-green-500",
  yellow: "bg-yellow-500",
  red: "bg-red-500",
  purple: "bg-purple-500",
  orange: "bg-orange-500",
  pink: "bg-pink-500",
};

export default function ProjectGroupsPage() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("active");
  const [customerFilter, setCustomerFilter] = useState<string>("all");
  const [showDeleted, setShowDeleted] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const { data: groups, isLoading } = useProjectGroups(showDeleted);
  const { data: companies } = useCompanies();
  const createGroup = useCreateProjectGroup();
  const softDelete = useSoftDeleteProjectGroup();
  const restore = useRestoreProjectGroup();
  const update = useUpdateProjectGroup();

  const filteredGroups =
    groups?.filter((group) => {
      const matchesSearch =
        group.name.toLowerCase().includes(search.toLowerCase()) ||
        group.number?.toLowerCase().includes(search.toLowerCase());

      const matchesStatus =
        statusFilter === "all" || group.status === statusFilter;
      const matchesCustomer =
        customerFilter === "all" || group.customer_id === customerFilter;
      const matchesDeleted = showDeleted ? true : !group.deleted_at;

      return (
        matchesSearch && matchesStatus && matchesCustomer && matchesDeleted
      );
    }) || [];

  const handleCreate = async (data: ProjectGroupInsert) => {
    try {
      await createGroup.mutateAsync(data);
      setIsCreateOpen(false);
    } catch (error) {
      // Error handled in hook
    }
  };

  const handleArchive = async (group: ProjectGroupWithRelations) => {
    try {
      await update.mutateAsync({
        id: group.id,
        updates: { status: "archived" as const },
      });
    } catch (error) {
      // Error handled in hook
    }
  };

  const handleUnarchive = async (group: ProjectGroupWithRelations) => {
    try {
      await update.mutateAsync({
        id: group.id,
        updates: { status: "active" as const },
      });
    } catch (error) {
      // Error handled in hook
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await softDelete.mutateAsync(id);
    } catch (error) {
      // Error handled in hook
    }
  };

  const handleRestore = async (id: string) => {
    try {
      await restore.mutateAsync(id);
    } catch (error) {
      // Error handled in hook
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader title="Project Groups">
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              New Group
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Create Project Group</DialogTitle>
            </DialogHeader>
            <ProjectGroupForm
              onSubmit={handleCreate}
              isLoading={createGroup.isPending}
            />
          </DialogContent>
        </Dialog>
      </PageHeader>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search groups..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
        <Select value={customerFilter} onValueChange={setCustomerFilter}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Customer" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Customers</SelectItem>
            {companies?.map((company) => (
              <SelectItem key={company.id} value={company.id}>
                {company.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant={showDeleted ? "secondary" : "outline"}
          onClick={() => setShowDeleted(!showDeleted)}
        >
          <Trash2 className="h-4 w-4 mr-2" />
          {showDeleted ? "Hide Deleted" : "Show Deleted"}
        </Button>
      </div>

      {/* Groups List */}
      {isLoading ? (
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-muted-foreground">Loading...</p>
          </CardContent>
        </Card>
      ) : filteredGroups.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Layers className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No project groups found.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredGroups.map((group) => (
            <Card
              key={group.id}
              className={`relative cursor-pointer hover:bg-accent/50 transition-colors ${
                group.deleted_at ? "opacity-60" : ""
              }`}
            >
              {!group.deleted_at && (
                <Link
                  to={`/projects/groups/${group.id}`}
                  className="absolute inset-0 z-0"
                  aria-label={`Open ${group.name}`}
                />
              )}
              <CardContent className="relative z-10 p-4 pointer-events-none">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {group.color && (
                      <div
                        className={`w-3 h-3 rounded-full ${COLOR_CLASSES[group.color] || "bg-gray-500"}`}
                      />
                    )}
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{group.name}</span>
                        {group.number && (
                          <span className="text-xs text-muted-foreground">
                            ({group.number})
                          </span>
                        )}
                        <Badge
                          variant={
                            group.status === "active" ? "default" : "secondary"
                          }
                        >
                          {group.status}
                        </Badge>
                        {group.deleted_at && (
                          <Badge variant="destructive">Deleted</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                        {group.companies && (
                          <span className="flex items-center gap-1">
                            <Building2 className="h-3 w-3" />
                            {group.companies.name}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <FolderOpen className="h-3 w-3" />
                          {group.projects?.length || 0} projects
                        </span>
                      </div>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger
                      asChild
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button
                        variant="ghost"
                        size="icon"
                        className="pointer-events-auto"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {group.deleted_at ? (
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRestore(group.id);
                          }}
                        >
                          <ArchiveRestore className="h-4 w-4 mr-2" />
                          Restore
                        </DropdownMenuItem>
                      ) : (
                        <>
                          <DropdownMenuItem asChild>
                            <Link to={`/projects/groups/${group.id}`}>
                              <Pencil className="h-4 w-4 mr-2" />
                              Edit
                            </Link>
                          </DropdownMenuItem>
                          {group.status === "active" ? (
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleArchive(group);
                              }}
                            >
                              <Archive className="h-4 w-4 mr-2" />
                              Archive
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUnarchive(group);
                              }}
                            >
                              <ArchiveRestore className="h-4 w-4 mr-2" />
                              Unarchive
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            className="text-destructive"
                            offlineEdit
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(group.id);
                            }}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
